import React, { useState } from "react";
import {
  TextField,
  Box,
  Modal,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Switch,
} from "@mui/material";
import {
  SearchIcon,
  Plus,
  Settings,
  RotateCw,
  X,
  FilterX,
  GripVertical,
  ListIcon,
  Trash,
} from "lucide-react";
import BrandCard from "./Components/BrandComponents/BrandCard";
import BreadCrumbsContainer from "../../Common/Breadcrumbs";
import BrandListItem from "./Components/BrandComponents/BrandListItem";
import BrandForm from "./Components/BrandComponents/BrandForm";
import ViewBrandModal from "./Components/BrandComponents/ViewBrandModal";
import {
  useCreateBrandMutation,
  useFetchBrandsQuery,
  useEditBrandMutation,
  useDeleteBrandMutation,
  useRestoreBrandMutation,
} from "../../../services/baseQuery";
import { errorToast, successToast } from "../../../services/toast.service";
import StatusConfirmationModal from "./Components/BrandComponents/StatusConfirmationModal";
import DeleteConfirmationModal from "./Components/BrandComponents/DeleteConfirmationModal";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  maxHeight: "90vh",
  overflow: "auto",
};

const Brand = () => {
  // State management
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("grid");
  const [anchorEl, setAnchorEl] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [queryParams, setQueryParams] = useState({
    status: "ALL",
    brand_type: "ALL",
    search: "",
    sort: "",
    include_deleted: false,
  });
  const [viewModal, setViewModal] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    brandtype: "DOMESTIC",
    orderValidation: {
      validationType: "NONE",
    },
  });
  const [editModal, setEditModal] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    description: "",
    brandtype: "DOMESTIC",
    orderValidation: {
      validationType: "NONE",
    },
  });
  const [editImage, setEditImage] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(null);
  const [updatingBrandId, setUpdatingBrandId] = useState(null);
  const [statusConfirmation, setStatusConfirmation] = useState({
    open: false,
    brand: null,
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    open: false,
    brand: null,
    isRestore: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [createBrand, { isLoading: isCreating }] = useCreateBrandMutation();
  const [editBrand, { isLoading: isEditing }] = useEditBrandMutation();
  const [deleteBrand, { isLoading: isDeleting }] = useDeleteBrandMutation();
  const [restoreBrand, { isLoading: isRestoring }] = useRestoreBrandMutation();

  // Fetch brands using the query
  const {
    data: brandsData,
    isLoading,
    isError,
    refetch: refetchBrands,
  } = useFetchBrandsQuery(queryParams);

  // Transform API data
  const brands = brandsData?.data?.brands || [];

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleViewBrand = brand => {
    setSelectedBrand(brand);
    setViewModal(true);
  };

  const handleAddBrand = async () => {
    try {
      if (!formData.name.trim()) {
        errorToast("Brand name is required");
        return;
      }

      // Create FormData object
      const submitFormData = new FormData();
      submitFormData.append("name", formData.name);
      submitFormData.append("brand_type", formData.brandtype);

      if (formData.description) {
        submitFormData.append("description", formData.description);
      }

      if (formData.orderValidation?.validationType === "MOQ_MOV") {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify({
            validationType: formData.orderValidation.validationType,
            minOrderQty: formData.orderValidation.minOrderQty,
            minOrderValue: formData.orderValidation.minOrderValue,
            validationCondition: formData.orderValidation.validationCondition,
          }),
        );
      } else {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify({
            validationType: "NONE",
          }),
        );
      }

      if (selectedImage) {
        submitFormData.append("image", selectedImage);
      }

      const response = await createBrand(submitFormData).unwrap();

      console.log(response, "<<< response");

      if (response.success) {
        successToast("Brand created successfully");
        setAddModal(false);
        // Reset form
        setFormData({
          name: "",
          description: "",
          brandtype: "DOMESTIC",
          orderValidation: {
            validationType: "NONE",
          },
        });
        setSelectedImage(null);
        setPreviewUrl(null);
      } else {
        errorToast(response.message || "Failed to create brand");
      }
    } catch (error) {
      console.error("Error creating brand:", error);
      errorToast(error.data?.error?.message || "Failed to create brand");
    }
  };

  const handleEditClick = brand => {
    setEditingBrand(brand);
    setEditFormData({
      name: brand.name,
      description: brand.description || "",
      brandtype: brand.brand_type,
      orderValidation: brand.orderValidation || { validationType: "NONE" },
    });
    setEditPreviewUrl(brand.image);
    setEditModal(true);
  };

  const handleEditBrand = async () => {
    try {
      if (!editFormData.name.trim()) {
        errorToast("Brand name is required");
        return;
      }

      const submitFormData = new FormData();
      submitFormData.append("name", editFormData.name);
      submitFormData.append("brand_type", editFormData.brandtype);
      submitFormData.append("status", editingBrand.status);

      if (editFormData.description) {
        submitFormData.append("description", editFormData.description);
      }

      if (editFormData.orderValidation?.validationType === "MOQ_MOV") {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify(editFormData.orderValidation),
        );
      } else {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify({
            validationType: "NONE",
          }),
        );
      }

      if (editImage) {
        submitFormData.append("image", editImage);
      }

      const response = await editBrand({
        brandId: editingBrand._id,
        formData: submitFormData,
      }).unwrap();

      if (response.success) {
        successToast("Brand updated successfully");
        setEditModal(false);
        resetEditForm();
      } else {
        errorToast(response.message || "Failed to update brand");
      }
    } catch (error) {
      console.error("Error updating brand:", error);
      errorToast(error.data?.error?.message || "Failed to update brand");
    }
  };

  const handleStatusToggle = async brand => {
    // If in edit modal, update directly
    if (editModal) {
      const newStatus = brand.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      setEditingBrand(prev => ({ ...prev, status: newStatus }));
      return;
    }

    // Otherwise show confirmation modal
    setStatusConfirmation({
      open: true,
      brand,
    });
  };

  const confirmStatusChange = async () => {
    const brand = statusConfirmation.brand;
    try {
      setUpdatingBrandId(brand._id);
      const newStatus = brand.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";

      const submitFormData = new FormData();
      submitFormData.append("name", brand.name);
      submitFormData.append("brand_type", brand.brand_type);
      submitFormData.append("status", newStatus);

      if (brand.description) {
        submitFormData.append("description", brand.description);
      }

      if (brand.orderValidation?.validationType === "MOQ_MOV") {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify(brand.orderValidation),
        );
      } else {
        submitFormData.append(
          "orderValidationJson",
          JSON.stringify({
            validationType: "NONE",
          }),
        );
      }

      const response = await editBrand({
        brandId: brand._id,
        formData: submitFormData,
      }).unwrap();

      if (response.success) {
        successToast(
          `Brand ${
            newStatus === "ACTIVE" ? "activated" : "deactivated"
          } successfully`,
        );
        setStatusConfirmation({ open: false, brand: null });
      } else {
        errorToast(response.message || "Failed to update brand status");
      }
    } catch (error) {
      console.error("Error updating brand status:", error);
      errorToast(error.data?.error?.message || "Failed to update brand status");
    } finally {
      setUpdatingBrandId(null);
    }
  };

  const resetEditForm = () => {
    setEditingBrand(null);
    setEditFormData({
      name: "",
      description: "",
      brandtype: "DOMESTIC",
      orderValidation: {
        validationType: "NONE",
      },
    });
    setEditImage(null);
    setEditPreviewUrl(null);
  };

  // Add refresh handler
  const handleRefresh = async () => {
    try {
      await refetchBrands();
      successToast("Product brands refreshed");
    } catch (error) {
      errorToast("Failed to refresh product brands");
    }
  };

  const handleDeleteClick = brand => {
    setDeleteConfirmation({
      open: true,
      brand,
      isRestore: false,
    });
  };

  const handleRestoreClick = brand => {
    setDeleteConfirmation({
      open: true,
      brand,
      isRestore: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const brand = deleteConfirmation.brand;
    try {
      const mutation = deleteConfirmation.isRestore
        ? restoreBrand
        : deleteBrand;
      const response = await mutation(brand._id).unwrap();

      if (response.success) {
        successToast(
          `Brand ${
            deleteConfirmation.isRestore ? "restored" : "deleted"
          } successfully`,
        );
        setDeleteConfirmation({ open: false, brand: null, isRestore: false });
      } else {
        errorToast(
          response.message ||
            `Failed to ${
              deleteConfirmation.isRestore ? "restore" : "delete"
            } brand`,
        );
      }
    } catch (error) {
      console.error("Error handling brand:", error);
      errorToast(
        error.data?.error?.message ||
          `Failed to ${
            deleteConfirmation.isRestore ? "restore" : "delete"
          } brand`,
      );
    }
  };

  // Update queryParams when switching between normal and deleted brands
  const handleShowDeletedChange = event => {
    setQueryParams(prev => ({
      ...prev,
      include_deleted: event.target.value,
      // Reset filters when switching views
      status: "ALL",
      brand_type: "ALL",
      search: "",
      sort: "",
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5652B0]" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)]">
        <p className="text-red-500 mb-2">Failed to load brands</p>
        <button
          onClick={refetchBrands}
          className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3]">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      <BreadCrumbsContainer />

      {/* Header Section - Keep original structure */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Product Brands
          </h1>
          <button
            onClick={() => setAddModal(true)}
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] flex items-center gap-2">
            <Plus size={16} />
            Add New Brand
          </button>
        </div>

        {/* Search and Actions Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={queryParams.search}
                onChange={e =>
                  setQueryParams(prev => ({ ...prev, search: e.target.value }))
                }
                placeholder="Search brands by name..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {queryParams.search && (
                <button
                  onClick={() =>
                    setQueryParams(prev => ({ ...prev, search: "" }))
                  }
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              className="hover:bg-gray-100"
              title="Refresh"
              onClick={handleRefresh}>
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="View Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      {/* <div className="px-6 py-4 border-b border-gray-200"> */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          {/* Left side filters */}
          <div className="flex items-center gap-4">
            {/* Status Filter - Only show for active brands */}
            {!queryParams.include_deleted && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={queryParams.status}
                  label="Status"
                  onChange={e =>
                    setQueryParams(prev => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }>
                  <MenuItem value="ALL">All Status</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Brand Type Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Brand Type</InputLabel>
              <Select
                value={queryParams.brand_type}
                label="Brand Type"
                onChange={e =>
                  setQueryParams(prev => ({
                    ...prev,
                    brand_type: e.target.value,
                  }))
                }>
                <MenuItem value="ALL">All Types</MenuItem>
                <MenuItem value="DOMESTIC">Domestic</MenuItem>
                <MenuItem value="INTERNATIONAL">International</MenuItem>
              </Select>
            </FormControl>

            {/* Sort Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={queryParams.sort}
                label="Sort By"
                onChange={e =>
                  setQueryParams(prev => ({ ...prev, sort: e.target.value }))
                }>
                <MenuItem value="">None</MenuItem>
                <MenuItem value="name_asc">Name (A-Z)</MenuItem>
                <MenuItem value="name_desc">Name (Z-A)</MenuItem>
                <MenuItem value="created_at_asc">Oldest First</MenuItem>
                <MenuItem value="created_at_desc">Newest First</MenuItem>
              </Select>
            </FormControl>
          </div>

          {/* Right side filters */}
          <div className="flex items-center gap-4">
            {/* Clear Filters Button */}
            {(queryParams.search ||
              queryParams.status !== "ALL" ||
              queryParams.brand_type !== "ALL" ||
              queryParams.sort) && (
              <button
                onClick={() =>
                  setQueryParams({
                    status: "ALL",
                    brand_type: "ALL",
                    search: "",
                    sort: "",
                    include_deleted: queryParams.include_deleted,
                  })
                }
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}

            {/* Vertical Divider */}
            <div className="h-8 w-px bg-gray-200" />

            {/* View Filter */}
            <FormControl
              size="small"
              sx={{
                minWidth: 180,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: queryParams.include_deleted
                    ? "rgb(254, 242, 242)"
                    : "white",
                  "& fieldset": {
                    borderColor: queryParams.include_deleted
                      ? "rgb(239, 68, 68)"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                  "&:hover fieldset": {
                    borderColor: queryParams.include_deleted
                      ? "rgb(239, 68, 68)"
                      : "rgba(0, 0, 0, 0.23)",
                  },
                },
              }}>
              <InputLabel>View</InputLabel>
              <Select
                value={queryParams.include_deleted}
                label="View"
                onChange={handleShowDeletedChange}>
                <MenuItem value={false}>Non Deleted Brands</MenuItem>
                <MenuItem value={true}>Deleted Brands</MenuItem>
              </Select>
            </FormControl>
          </div>
        </div>
      </div>

      {/* Deleted Brands Banner */}
      {queryParams.include_deleted && (
        <div className="mx-6 mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
              <Trash size={18} className="text-red-600" />
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">
                Viewing Deleted Brands
              </h3>
              <p className="text-sm text-red-600">
                These brands are in trash and hidden from customers
              </p>
            </div>
          </div>
          <button
            onClick={() =>
              setQueryParams({
                status: "ALL",
                brand_type: "ALL",
                search: "",
                sort: "",
                include_deleted: false,
              })
            }
            className="px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 bg-red-100 hover:bg-red-200 rounded-lg transition-colors">
            View Non Deleted Brands
          </button>
        </div>
      )}

      {/* Content Section with proper spacing */}
      <div className="px-6">
        {/* No Results Message */}
        {brands.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {queryParams.include_deleted
                ? "No deleted brands found"
                : "No brands found"}
            </p>
          </div>
        )}

        {/* Brands Grid/List */}
        {viewType === "grid" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {brands.map(brand => (
              <BrandCard
                key={brand._id}
                brand={brand}
                onView={handleViewBrand}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onRestore={handleRestoreClick}
                isDeleted={brand.isDeleted}
                onStatusChange={handleStatusToggle}
                isStatusUpdating={updatingBrandId === brand._id}
              />
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200">
            {brands.map(brand => (
              <BrandListItem
                key={brand._id}
                brand={brand}
                onView={handleViewBrand}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
                onRestore={handleRestoreClick}
                isDeleted={brand.isDeleted}
                onStatusChange={handleStatusToggle}
                isStatusUpdating={updatingBrandId === brand._id}
              />
            ))}
          </div>
        )}
      </div>

      {/* Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSettings}>
        <MenuItem
          onClick={() => {
            setViewType("grid");
            handleCloseSettings();
          }}>
          <GripVertical size={16} className="mr-2" />
          Grid View
        </MenuItem>
        <MenuItem
          onClick={() => {
            setViewType("list");
            handleCloseSettings();
          }}>
          <ListIcon size={16} className="mr-2" />
          List View
        </MenuItem>
      </Menu>

      {/* Add Brand Modal */}
      <Modal open={addModal} onClose={() => setAddModal(false)}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Add New Brand
              </h2>
              <IconButton size="small" onClick={() => setAddModal(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <BrandForm
              formData={formData}
              setFormData={setFormData}
              selectedImage={selectedImage}
              handleImageSelect={handleImageSelect}
              previewUrl={previewUrl}
              setPreviewUrl={setPreviewUrl}
              setSelectedImage={setSelectedImage}
            />

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setAddModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                disabled={isCreating}>
                Cancel
              </button>
              <button
                onClick={handleAddBrand}
                disabled={isCreating}
                className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {isCreating ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Add Brand"
                )}
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* View Brand Modal */}
      <ViewBrandModal
        brand={selectedBrand}
        open={viewModal}
        onClose={() => {
          setViewModal(false);
          setSelectedBrand(null);
        }}
      />

      {/* Edit Brand Modal */}
      <Modal
        open={editModal}
        onClose={() => {
          setEditModal(false);
          resetEditForm();
        }}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">Edit Brand</h2>
              <IconButton
                size="small"
                onClick={() => {
                  setEditModal(false);
                  resetEditForm();
                }}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <BrandForm
              formData={editFormData}
              setFormData={setEditFormData}
              selectedImage={editImage}
              handleImageSelect={e => {
                const file = e.target.files[0];
                if (file) {
                  setEditImage(file);
                  setEditPreviewUrl(URL.createObjectURL(file));
                }
              }}
              previewUrl={editPreviewUrl}
              setPreviewUrl={setEditPreviewUrl}
              setSelectedImage={setEditImage}
            />

            <div className="flex justify-between items-center mt-6">
              <div className="flex items-center">
                <Switch
                  checked={editingBrand?.status === "ACTIVE"}
                  onChange={() => handleStatusToggle(editingBrand)}
                  disabled={isEditing}
                  sx={{
                    "&.Mui-checked": {
                      color: "#5652B0",
                    },
                    "&.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#5652B0",
                    },
                  }}
                />
                <div className="ml-2">
                  <p className="text-sm font-medium text-gray-900">
                    {editingBrand?.status === "ACTIVE" ? "Active" : "Inactive"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {editingBrand?.status === "ACTIVE"
                      ? "Brand and its products are visible to customers"
                      : "Brand and its products are hidden from customers"}
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditModal(false);
                    resetEditForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                  disabled={isEditing}>
                  Cancel
                </button>
                <button
                  onClick={handleEditBrand}
                  disabled={isEditing}
                  className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                  {isEditing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </button>
              </div>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Status Confirmation Modal */}
      <StatusConfirmationModal
        open={statusConfirmation.open}
        onClose={() => setStatusConfirmation({ open: false, brand: null })}
        onConfirm={confirmStatusChange}
        brand={statusConfirmation.brand}
        isLoading={updatingBrandId === statusConfirmation.brand?._id}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={deleteConfirmation.open}
        onClose={() =>
          setDeleteConfirmation({ open: false, brand: null, isRestore: false })
        }
        onConfirm={handleDeleteConfirm}
        brand={deleteConfirmation.brand}
        isLoading={isDeleting || isRestoring}
        isRestore={deleteConfirmation.isRestore}
      />
    </div>
  );
};

export default Brand;
